import React, { ReactElement } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { toFixedHours } from '@app/utils';

const formatEur = (amount: number): string =>
  new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);

/**
 * A sprint's headline figures, shared by the client's report and ours. On one line, level with the date,
 * so the summary under them keeps the card's full width. No cost while it is unknown: the hours stand alone.
 */
const SprintTotals = ({
  totalMinutes,
  totalCost,
  unratedHours = 0,
  priced: pricedAnything,
  costHref,
}: {
  totalMinutes: number;
  totalCost?: number;
  /** Hours logged by people with no rate, left unpriced rather than billed at a guess. */
  unratedHours?: number;
  /** Whether anything was priced before a discount, so a sprint discounted to nothing still shows its €0. */
  priced?: boolean;
  /** Where the cost leads, in a new tab so the report keeps its place; no link when absent. */
  costHref?: string;
}): ReactElement => {
  const { t } = useTranslation();
  // A €0 beside logged hours reads as free work, so nothing priced shows a dash instead.
  const priced = totalCost !== undefined && (pricedAnything ?? (totalCost !== 0 || unratedHours === 0));
  // A hover rather than a line under the figure: a second line would push the summary down.
  const unratedNote = unratedHours > 0 ? t('unratedHoursNote', { hours: Math.round(unratedHours) }) : undefined;
  const cost = (
    <>
      <span className="pr-2 text-muted-foreground">{t('titles.totalCost')}:</span>
      <span
        className={cx(
          'font-semibold tabular-nums text-foreground',
          costHref && 'underline decoration-1 underline-offset-2 group-hover:decoration-2',
        )}
        title={priced ? unratedNote : t('noRateSetHint')}
      >
        {priced ? formatEur(totalCost ?? 0) : '—'}
      </span>
    </>
  );
  return (
    <div className="flex shrink-0 items-baseline gap-6 text-sm">
      {totalCost !== undefined &&
        (costHref ? (
          <a
            href={costHref}
            target="_blank"
            rel="noopener noreferrer"
            className="group rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {cost}
          </a>
        ) : (
          <span>{cost}</span>
        ))}

      <span>
        <span className="pr-2 text-muted-foreground">{t('titles.totalHours')}:</span>
        <span className="font-semibold tabular-nums text-foreground">{toFixedHours(totalMinutes / 60)}h</span>
      </span>
    </div>
  );
};

export default SprintTotals;
